'use client'

import { HorizontalDots24, Pencil24 } from '@vinted/monochrome-icons'
import { Button, Cell, Icon, Image, Rating, Spacer } from '@vinted/web-ui'
import classNames from 'classnames'
import { noop } from 'lodash'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { businessAccountFromUserDto } from '@marketplace-web/business-accounts/business-accounts-data'
import { SellerBadge } from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-feature'
import { SellerBadgeDto } from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { setSessionStorageItem } from '@marketplace-web/browser/browser-storage-util'
import { navigateToPage, reloadPage } from '@marketplace-web/shared/browser-util'
import { scrollToElementById } from '@marketplace-web/browser/browser-interaction-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import { urlWithParams } from '@marketplace-web/shared/utils'
import { UserTitle } from '@marketplace-web/common-components/user-title-ui'
import { REPORT_SESSION_ID_KEY, AlertType } from '@marketplace-web/admin-alert/admin-alert-data'
import {
  clickEvent,
  followUserEvent,
  blockUser,
  unblockUser,
  ProfileUserDto,
} from '@marketplace-web/profile/profile-data'
import DropdownMenu, { DropdownActions } from '@marketplace-web/profile/dropdown-menu-ui'
import FollowButton from '@marketplace-web/user-follow/follow-button-feature'
import { UserBlockModal } from '@marketplace-web/conversation-safety/conversation-safety-feature'
import { UnauthenticatedReportModal } from '@marketplace-web/personal-information/personal-information-feature'

import { NEW_ADMIN_ALERT_URL, PROFILE_SETTINGS_URL } from '../../../constants/routes'
import UserDescription from './UserDescription'
import UserInfo from './UserInfo'
import UserStatuses from './UserStatuses'
import { calculateRating } from '../../../utils/user'
import { isDateInTheFuture } from '../../../utils/date'

type Props = {
  user: ProfileUserDto & { seller_badges?: Array<Omit<SellerBadgeDto, 'progress'>> }
  sellerBadgesList: Array<Omit<SellerBadgeDto, 'progress'>> | undefined
  viewingSelf: boolean
  showActions: boolean
  showSupportFunctionality: boolean
  canSendTemplates: boolean
  screen: string
  isShadowBanned?: boolean
  isPermanentlyBanned?: boolean
  isTemporarilyBanned?: boolean
  isCompromised?: boolean
  isListingRestricted?: boolean
  isSellingRestricted?: boolean
  isContentTranslationEnabled: boolean
  onTogglePoliciesModal?: () => void
  onRatingClick?: () => void
}

const InfoHeader = ({
  user,
  sellerBadgesList,
  viewingSelf,
  showActions,
  showSupportFunctionality,
  canSendTemplates,
  screen,
  isShadowBanned,
  isPermanentlyBanned,
  isTemporarilyBanned,
  isCompromised,
  isListingRestricted,
  isSellingRestricted,
  isContentTranslationEnabled,
  onTogglePoliciesModal = noop,
  onRatingClick = noop,
}: Props) => {
  const breakpoints = useBreakpoint()
  const { relativeUrl } = useBrowserNavigation()
  const translate = useTranslate('user')
  const businessTranslate = useTranslate()
  const { track } = useTracking()
  const asset = useAsset('assets/no-photo')
  const { id: currentUserId } = useSession().user || {}

  const isActionsVisible = showActions && !user.is_account_banned

  const isProfileBlockingImprovementsEnabled = useFeatureSwitch('profile_blocking_improvements')

  const isProfileReportingModalEnabled = useFeatureSwitch('ntd_form_user')

  const [isBlockModalVisible, setIsBlockModalVisible] = useState(false)
  const [isReportModalVisible, setIsReportModalVisible] = useState(false)
  const isFollowTracked = useRef(false)

  if (breakpoints.phones) return null

  const photoUrl = user.photo?.url || asset(EMPTY_USER_IMAGE_NAME)
  const rating = calculateRating(user.feedback_reputation)

  const toggleBlock = async () => {
    if (!currentUserId) return

    const toggleFunction = user.is_hated ? unblockUser : blockUser

    await toggleFunction({ currentUserId, blockedUserId: user.id })
    reloadPage()
  }

  const trackFollowToggle = ({ isFollowEvent }: { isFollowEvent: boolean }) => {
    track(followUserEvent({ userId: user.id, isFollowEvent }))

    if (isFollowTracked.current) return

    isFollowTracked.current = true
    track(clickEvent({ target: 'follow', screen: 'user_profile' }))
  }

  function handleBlockCancel() {
    setIsBlockModalVisible(false)
  }

  function handleModalBlockButtonClick() {
    setIsBlockModalVisible(false)
    reloadPage()
  }

  function handleBlockClick() {
    if (isProfileBlockingImprovementsEnabled) {
      setIsBlockModalVisible(true)

      return
    }

    toggleBlock()
  }

  function handleRatingsClick() {
    onRatingClick()
    scrollToElementById('tabs')
  }

  function handleReportClick() {
    if (currentUserId || !isProfileReportingModalEnabled) {
      const url = urlWithParams(NEW_ADMIN_ALERT_URL, {
        ref_id: user.id,
        ref_type: AlertType.Member,
        ref_url: relativeUrl,
      })

      setSessionStorageItem(REPORT_SESSION_ID_KEY, uuid())

      track(
        clickEvent({
          screen: 'user_profile',
          target: 'report_profile',
          targetDetails: String(user.id),
        }),
      )

      navigateToPage(url)

      return
    }

    setIsReportModalVisible(true)
  }

  function handleReportModalClose() {
    setIsReportModalVisible(false)
  }

  function renderEditProfileAction() {
    if (!viewingSelf) return null

    return (
      <Button
        icon={<Icon name={Pencil24} color={Icon.Color.GreyscaleLevel2} />}
        url={PROFILE_SETTINGS_URL}
        text={translate('action.edit_profile')}
        theme="muted"
        size={Button.Size.Medium}
        inline
      />
    )
  }

  function renderFollowAction() {
    if (viewingSelf || !isActionsVisible) return null
    if (isDateInTheFuture(user.closet_countdown_end_date)) return null

    return (
      <div>
        <FollowButton
          onFollowToggle={trackFollowToggle}
          userId={user.id}
          isActive={user.is_favourite}
          size={Button.Size.Medium}
          testId="profile-info-follow-button"
        />
      </div>
    )
  }

  function renderExtraActions() {
    if (viewingSelf && !showSupportFunctionality) return null

    const actions: Array<DropdownActions> = [
      {
        id: 'report',
        text: translate('action.report'),
        onClick: handleReportClick,
      },
    ]

    if (currentUserId) {
      actions.push({
        id: user.is_hated ? 'unblock' : 'block',
        text: translate(user.is_hated ? 'action.unblock' : 'action.block'),
        onClick: handleBlockClick,
      })
    }

    const dropdownButtonProps = {
      styling: Button.Styling.Flat,
      icon: (
        <Icon
          testId="actions-menu-button"
          name={HorizontalDots24}
          color={Icon.Color.GreyscaleLevel2}
          title={translate('a11y.show_more')}
        />
      ),
      size: Button.Size.Medium,
    }

    return (
      <div>
        <DropdownMenu actions={actions} buttonProps={dropdownButtonProps} />
      </div>
    )
  }

  function renderMainActions() {
    return (
      <div className="u-flexbox">
        <SeparatedList
          separator={<Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Large} />}
        >
          {renderEditProfileAction()}
          {renderFollowAction()}
        </SeparatedList>
      </div>
    )
  }

  function renderAllActions() {
    return (
      <div className="u-flexbox">
        <SeparatedList
          separator={<Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Large} />}
        >
          {renderEditProfileAction()}
          {renderFollowAction()}
          {renderExtraActions()}
        </SeparatedList>
      </div>
    )
  }

  function renderHeaderContent() {
    return (
      <>
        <button type="button" onClick={handleRatingsClick} data-testid="rating-button">
          <Rating
            aria={{
              'aria-label': (rating && translate('a11y.rating', { rating, max_rating: 5 })) || '',
            }}
            value={rating}
            text={translate(
              'feedback.reviews',
              { count: user.feedback_count },
              { count: user.feedback_count },
            )}
            emptyStateText={translate('feedback.no_reviews')}
          />
        </button>
        {breakpoints.tablets && (
          <>
            <Spacer />
            {renderMainActions()}
          </>
        )}
      </>
    )
  }

  function renderHeaderSuffix() {
    return breakpoints.desktops ? renderAllActions() : renderExtraActions()
  }

  function renderUserBlockModal() {
    return (
      <UserBlockModal
        username={user.login}
        isUserBlocked={user.is_hated}
        userId={user.id}
        isVisible={isBlockModalVisible}
        onCancel={handleBlockCancel}
        onConfirm={handleModalBlockButtonClick}
      />
    )
  }

  const renderSellerBadges = () => {
    if (!user) return null

    if (!sellerBadgesList?.length) return null

    const containerClass = classNames('u-flexbox', {
      'u-flex-direction-column': breakpoints.tablets,
    })

    const renderSeparator = () => {
      return breakpoints.desktops ? (
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.X3Large} />
      ) : (
        <Spacer orientation={Spacer.Orientation.Horizontal} size={Spacer.Size.Large} />
      )
    }

    return (
      <div className="u-flexbox">
        <Spacer size={Spacer.Size.Large} orientation={Spacer.Orientation.Vertical} />
        <div className={containerClass}>
          <SeparatedList separator={renderSeparator()}>
            {sellerBadgesList.map((badge, index) => (
              <SellerBadge type={badge.type} key={index} />
            ))}
          </SeparatedList>
        </div>
      </div>
    )
  }

  return (
    <div className="u-flexbox">
      <div>
        <Image src={photoUrl} styling={Image.Styling.Circle} size={Image.Size.X4Large} alt="" />
      </div>
      <Spacer size={Spacer.Size.Large} orientation={Spacer.Orientation.Vertical} />
      <div className="u-flex-grow">
        <Cell
          title={
            <UserTitle
              badgeTitle={businessTranslate('business.title')}
              businessAccountName={businessAccountFromUserDto(user)?.name}
              login={user.login}
              isBusiness={user.business}
            />
          }
          body={renderHeaderContent()}
          suffix={renderHeaderSuffix()}
        />
        {renderSellerBadges()}
        <UserStatuses
          onUnblockClick={isProfileBlockingImprovementsEnabled ? handleBlockClick : undefined}
          user={user}
          screen={screen}
          showSupportFunctionality={showSupportFunctionality}
          isShadowBanned={isShadowBanned}
          isPermanentlyBanned={isPermanentlyBanned}
          isTemporarilyBanned={isTemporarilyBanned}
          isCompromised={isCompromised}
          isListingRestricted={isListingRestricted}
          isSellingRestricted={isSellingRestricted}
          viewingSelf={viewingSelf}
        />
        <Cell>
          <UserInfo
            user={user}
            showSupportFunctionality={showSupportFunctionality}
            canSendTemplates={canSendTemplates}
            onTogglePoliciesModal={onTogglePoliciesModal}
            viewingSelf={viewingSelf}
          />
        </Cell>
        <UserDescription
          user={user}
          isContentTranslationEnabled={isContentTranslationEnabled}
          viewingSelf={viewingSelf}
        />
      </div>
      <UnauthenticatedReportModal
        isVisible={isReportModalVisible}
        onClose={handleReportModalClose}
      />
      {renderUserBlockModal()}
    </div>
  )
}

export default InfoHeader
